import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t } from 'ember-intl';
import {
  SMART_INSIGHTS_KPI_COMPARISON_PERIOD_TRANSLATIONS,
  type SmartInsightsKpiComparisonPeriod,
} from 'lumen/utils/constants/smart-insights';

import styles from './smart-insight-kpi-hover-panel.module.css';

type Nullable = number | null | undefined;

export interface KpiHoverRow {
  label: string;
  value: string;
}

// Raw numbers plus formatters: formatting happens in the panel so it only runs once the tooltip opens.
export interface KpiHoverData {
  format: (value: number) => string;
  formatPickup: (value: number) => string;
  // Only room nights has a rooms-left row, so the row shows whenever this flag is set, with a placeholder if the value is missing.
  hasRoomsLeftToSell?: boolean;
  lastYearFinal: Nullable;
  pickupPeriod: SmartInsightsKpiComparisonPeriod;
  pickupStly: Nullable;
  pickupThisYear: Nullable;
  roomsLeftToSell?: Nullable;
  stly: Nullable;
  thisYear: Nullable;
}

interface GeneralSmartInsightsDetailViewSmartInsightKpiHoverPanelSignature {
  Args: {
    data: KpiHoverData;
    title: string;
  };
}

// Must be rendered inside its trigger: EmberTooltip attaches to its parent element.
export default class GeneralSmartInsightsDetailViewSmartInsightKpiHoverPanel extends Component<GeneralSmartInsightsDetailViewSmartInsightKpiHoverPanelSignature> {
  @service declare intl: Services['intl'];

  @cached
  get pickupPeriodLabel(): string {
    return this.intl.t(
      SMART_INSIGHTS_KPI_COMPARISON_PERIOD_TRANSLATIONS[
        this.args.data.pickupPeriod
      ],
    );
  }

  @cached
  get pickupRows(): KpiHoverRow[] {
    const { formatPickup, pickupStly, pickupThisYear } = this.args.data;

    return [
      this.toRow(
        'smart_insights.detail_view.kpi_hover.this_year',
        pickupThisYear,
        formatPickup,
      ),
      this.toRow(
        'smart_insights.detail_view.kpi_hover.stly',
        pickupStly,
        formatPickup,
      ),
    ];
  }

  @cached
  get roomsLeftToSell(): string {
    const { format, roomsLeftToSell } = this.args.data;

    return this.formatOrMissing(roomsLeftToSell, format);
  }

  @cached
  get rows(): KpiHoverRow[] {
    const { format, lastYearFinal, stly, thisYear } = this.args.data;

    return [
      this.toRow(
        'smart_insights.detail_view.kpi_hover.this_year',
        thisYear,
        format,
      ),
      this.toRow('smart_insights.detail_view.kpi_hover.stly', stly, format),
      this.toRow(
        'smart_insights.detail_view.kpi_hover.last_year_final',
        lastYearFinal,
        format,
      ),
    ];
  }

  private formatOrMissing(
    value: Nullable,
    format: (value: number) => string,
  ): string {
    return value === null || value === undefined
      ? this.intl.t('general.missing')
      : format(value);
  }

  private toRow(
    labelKey: string,
    value: Nullable,
    format: (value: number) => string,
  ): KpiHoverRow {
    return {
      label: this.intl.t(labelKey),
      value: this.formatOrMissing(value, format),
    };
  }

  <template>
    <EmberTooltip @side="bottom" @tooltipClass={{styles.tooltip}}>
      <div class={{styles.panel}} data-test-id="smart-insight-kpi-hover-panel">
        <div class={{styles.section}}>
          <p class="paragraph-300--bold">{{@title}}</p>

          <div class={{styles.rows}}>
            {{#each this.rows as |row|}}
              <div
                class={{styles.row}}
                data-test-id="smart-insight-kpi-hover-row"
              >
                <span>{{row.label}}</span>
                <span class={{styles.value}}>{{row.value}}</span>
              </div>
            {{/each}}
          </div>
        </div>

        {{#if @data.hasRoomsLeftToSell}}
          <div class={{styles.divider}} />

          <div class={{styles.section}}>
            <div
              class={{styles.row}}
              data-test-id="smart-insight-kpi-hover-rooms-left"
            >
              <span>
                {{t "smart_insights.detail_view.kpi_hover.rooms_left_to_sell"}}
              </span>
              <span class={{styles.value}}>{{this.roomsLeftToSell}}</span>
            </div>
          </div>
        {{/if}}

        <div class={{styles.divider}} />

        <div class={{styles.section}}>
          <p
            class="{{styles.pickup}} paragraph-300--regular"
            data-test-id="smart-insight-kpi-hover-pickup-title"
          >
            <span class="paragraph-300--bold">
              {{t "smart_insights.detail_view.kpi_hover.pickup_title"}}
            </span>
            <span class="text-neutral-subdued">
              {{this.pickupPeriodLabel}}
            </span>
          </p>

          <div class={{styles.rows}}>
            {{#each this.pickupRows as |row|}}
              <div
                class={{styles.row}}
                data-test-id="smart-insight-kpi-hover-pickup-row"
              >
                <span>{{row.label}}</span>
                <span class={{styles.value}}>{{row.value}}</span>
              </div>
            {{/each}}
          </div>
        </div>
      </div>
    </EmberTooltip>
  </template>
}
